/* globals google */
import React, { useEffect, useMemo, useRef } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

const getInfoWindowString = ({
  shop_name: name,
  phone,
  address: { street, city, state },
}) => `
  <div class="container mx-auto">
    <h2 class="font-sans font-bold text-brand-blue text-xl">
      ${name !== null ? name : ""}
    </h2>
    <p class="text-brand-textgray font-sans">${street}</p>
    <p class="text-brand-textgray font-sans">${city}, ${state}</p>
    <p class="text-brand-textgray font-sans inline-block">${phone}</p>
  </div>
`;

const containerStyle = {
  width: "100%",
  height: "35rem", // matches your h-140 (35rem)
};

function Map({
  apiKey = "",
  mapId = "",
  shops = [],
  center,
  defaultCenter = { lat: 38.91388765000001, lng: -77.01372719437762 },
  defaultZoom = 12,
}) {
  // @react-google-maps/api expects a stable libraries array
  const libraries = useMemo(() => ["places", "marker"], []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowsRef = useRef([]);

  const clearMarkersAndWindows = () => {
    markersRef.current.forEach((m) => {
      // AdvancedMarkerElement removes via setting map to null
      if (m && "map" in m) m.map = null;
      // Legacy Marker remove
      if (m && typeof m.setMap === "function") m.setMap(null);
    });
    markersRef.current = [];

    infoWindowsRef.current.forEach((w) => {
      if (w && typeof w.close === "function") w.close();
    });
    infoWindowsRef.current = [];
  };

  const buildMarkers = () => {
    const map = mapRef.current;
    if (!map) return;
    if (!window.google?.maps) return;

    clearMarkersAndWindows();

    const AdvancedMarkerElement = google.maps?.marker?.AdvancedMarkerElement;

    shops.forEach((shop) => {
      const position = {
        // Keeping your existing (reversed) behavior:
        lng: shop.address.lat,
        lat: shop.address.long,
      };

      const infoWindow = new google.maps.InfoWindow({
        content: getInfoWindowString(shop),
      });
      infoWindowsRef.current.push(infoWindow);

      if (AdvancedMarkerElement) {
        const marker = new AdvancedMarkerElement({
          map,
          position,
          title: shop.shop_name,
        });

        marker.addListener("gmp-click", () => {
          infoWindowsRef.current.forEach((w) => w.close());
          infoWindow.open({
            map,
            anchor: marker,
            shouldFocus: false,
          });
        });

        markersRef.current.push(marker);
        return;
      }

      // Fallback (should be rare if marker lib loaded)
      const marker = new google.maps.Marker({
        map,
        position,
        title: shop.shop_name,
      });

      marker.addListener("click", () => {
        infoWindowsRef.current.forEach((w) => w.close());
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
    });
  };

  // Rebuild markers any time shops change
  useEffect(() => {
    if (!isLoaded) return;
    if (!mapRef.current) return;
    buildMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, shops]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearMarkersAndWindows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadError) {
    return <div className="w-full h-140">Failed to load Google Maps</div>;
  }

  if (!isLoaded) {
    return <div className="w-full h-140" />;
  }

  return (
    <div className="w-full h-140">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center && center.lat ? center : defaultCenter}
        zoom={defaultZoom}
        options={{
          mapId,
          // IMPORTANT: do NOT set `styles` when using mapId
        }}
        onLoad={(map) => {
          mapRef.current = map;
          buildMarkers();
        }}
        onUnmount={() => {
          clearMarkersAndWindows();
          mapRef.current = null;
        }}
      />
    </div>
  );
}

export default Map;
